// SeoHead — injects <title>, <meta description>, OG tags and hreflang alternates.
// Handles locale-specific content for each public page.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const SUPPORTED = ["fr", "en", "it", "es", "de", "pt", "zh", "pl", "ru"];

const HTML_LANG = {
  fr: "fr-FR", en: "en-GB", it: "it-IT", es: "es-ES",
  de: "de-DE", pt: "pt-PT", zh: "zh-CN", pl: "pl-PL", ru: "ru-RU",
};

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[(.+?)="(.+?)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function removeManagedTags() {
  document.head.querySelectorAll("[data-seo-managed]").forEach((el) => el.remove());
}

function appendLink(rel, href, hreflang) {
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  link.setAttribute("href", href);
  if (hreflang) link.setAttribute("hreflang", hreflang);
  link.setAttribute("data-seo-managed", "1");
  document.head.appendChild(link);
}

export default function SeoHead({ titles, descriptions, image, keywords }) {
  const { locale } = useI18n();
  const { pathname } = useLocation();

  useEffect(() => {
    const title = (titles && (titles[locale] || titles.fr)) || "Prieuré saint Léonard";
    const description = (descriptions && (descriptions[locale] || descriptions.fr)) || "";
    const origin = typeof window !== "undefined" ? window.location.origin : "https://prieure-saint-leonard.fr";
    const cleanPath = pathname || "/";

    document.title = `${title} — Prieuré saint Léonard`;
    document.documentElement.setAttribute("lang", HTML_LANG[locale] || "fr-FR");

    setMeta('meta[name="description"]', "content", description);
    if (keywords) setMeta('meta[name="keywords"]', "content", keywords);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:locale"]', "content", HTML_LANG[locale] || "fr_FR");
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:url"]', "content", `${origin}${cleanPath}`);
    if (image) setMeta('meta[property="og:image"]', "content", image.startsWith("http") ? image : `${origin}${image}`);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);

    // Refresh hreflang & canonical
    removeManagedTags();
    SUPPORTED.forEach((loc) => {
      appendLink("alternate", `${origin}${cleanPath}?lang=${loc}`, HTML_LANG[loc]);
    });
    appendLink("alternate", `${origin}${cleanPath}`, "x-default");
    appendLink("canonical", `${origin}${cleanPath}`);
  }, [locale, pathname, titles, descriptions, image, keywords]);

  return null;
}
